import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from '@/design-system';

import { surveyErrors } from '../copy';

interface Props {
    children: ReactNode;
}

interface State {
    error: Error | null;
}

/**
 * De opname is het scherm dat offline moet blijven staan. Een fout in het
 * werkvlak mag dus nooit het hele scherm wegvagen: de kop, de teller en de
 * voortgangsbalk blijven staan en de inspecteur krijgt één knop om verder te
 * gaan. De antwoorden staan al lokaal in Dexie, dus er gaat niets verloren.
 *
 * Er komt ook een globale error boundary in de app-schil; deze vangt wat in
 * het werkvlak zelf misgaat, zodat de globale niet hoeft in te grijpen.
 */
export class SurveyErrorBoundary extends Component<Props, State> {
    override state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    override componentDidCatch(error: Error, info: ErrorInfo): void {
        console.error('[opname] scherm liep vast', error, info.componentStack);
    }

    override render(): ReactNode {
        const { error } = this.state;

        if (!error) {
            return this.props.children;
        }

        return (
            <section
                role="alert"
                className="flex flex-col items-start gap-3 overflow-auto bg-[var(--surface-page)] px-[22px] pt-5 pb-10"
            >
                <h2 className="m-0 font-[family-name:var(--font-display)] text-[17px] font-semibold text-[var(--text-primary)]">
                    {surveyErrors.boundaryTitle}
                </h2>
                <p className="m-0 max-w-[520px] text-[14px] leading-[1.5] text-[var(--text-primary)]">
                    {surveyErrors.boundaryBody} {surveyErrors.keep}
                </p>
                <p className="m-0 max-w-[520px] text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {error.message}
                </p>
                <Button variant="primary" size="md" onClick={() => this.setState({ error: null })}>
                    {surveyErrors.retry}
                </Button>
            </section>
        );
    }
}
